// THE LENS IN THE URL: `?as=<lens key>`. Pure, so the proxy, the server and
// the browser read one spelling.
//
// The key is what the persona cookie holds (lib/persona-cookie.ts): `admin`, a
// person's slug, or `brand:<slug>`. A URL is a place an admin can go BACK to,
// and a cookie is not, which is the whole reason the lens is here as well:
// every history entry of an admin's names the lens it was drawn with, so Back
// and Forward step through lens switches like any other navigation.
//
// ⚠️ THE URL WINS, AND THE COOKIE FOLLOWS THE ENTRY BEING SHOWN. A request
// carrying `?as=` is rendered through that lens whatever the cookie says; the
// cookie is what a link WITHOUT `?as=` (the sidebar, a server page's own href)
// falls back to, so the browser rewrites it to the lens on screen
// (components/lens-url-sync.tsx). Neither is a privilege: both only SELECT one
// of the lenses the server built for the identity (`personaForKey`), an admin's
// or a staff seat's over their own brands, and anyone else is offered none.

import { query } from "./url-query";

/** The query param. */
export const LENS_PARAM = "as";

/**
 * The request header the proxy copies `?as=` onto, because a layout cannot read
 * `searchParams` and every lens reader (`activeLensFor`) has to see the same
 * value the page does. The proxy overwrites whatever a browser sent under this
 * name, so on a page request it can only ever say what the URL says.
 */
export const LENS_HEADER = "x-ironside-lens";

/**
 * What a lens key can look like: a slug, or `brand:` and a slug. Anything else
 * is not forwarded at all — it names no lens, and a header value is no place
 * for arbitrary bytes from a URL.
 */
const KEY_SHAPE = /^(brand:)?[a-z0-9-]{1,120}$/;

export function isLensKeyShaped(v: string | null | undefined): v is string {
  return typeof v === "string" && KEY_SHAPE.test(v);
}

/**
 * The address you are on, with the lens set to `key` — or with `?as=` removed
 * when `key` is null. Every other param keeps its place, and so does the hash.
 * A lens that was already in the address stays where it was, and a new one goes
 * FIRST: `?as=admin&brand=saphira` reads as what it is, a lens and then what
 * that lens picked, and one position means one spelling per address.
 *
 * Spelled by hand (lib/url-query.ts) so a brand key keeps its `:`.
 */
export function withLens(
  here: { pathname: string; params: Iterable<[string, string]>; hash: string },
  key: string | null,
): string {
  const pairs: [string, string][] = [];
  let placed = false;
  for (const [k, v] of here.params) {
    if (k !== LENS_PARAM) {
      pairs.push([k, v]);
      continue;
    }
    if (key !== null && !placed) pairs.push([k, key]);
    placed = true;
  }
  if (!placed && key !== null) pairs.unshift([LENS_PARAM, key]);
  return `${here.pathname}${query(pairs)}${here.hash}`;
}

/**
 * A LINK A SERVER PAGE DRAWS, WITH THE LENS IT IS DRAWN THROUGH — `href` with
 * `?as=<key>` first in its query, or `href` untouched when `key` is null
 * (anyone holding no lens, whose addresses carry none).
 *
 * ⚠️ WHY A SERVER PAGE'S OWN LINKS CARRY IT. A link with no `?as=` still opens in
 * the lens on screen, because the server falls back to the cookie — but the
 * browser then stamps the key onto the address with a `router.replace`
 * (components/lens-url-sync.tsx), so every click on a tab, a pill or a pager
 * cost a push AND a replace: two server renders for one click. A link that
 * already names its lens is one.
 *
 * `key` is the lens the page was drawn through — `activeLensFor(identity).key`,
 * which `currentLensAndKey()` hands a page — never the raw `?as=`: a key the
 * roster does not offer resolves to the identity, and the links then name the
 * lens actually on screen.
 */
export function lensHref(href: string, key: string | null): string {
  if (key === null) return href;
  const hashAt = href.indexOf("#");
  const hash = hashAt === -1 ? "" : href.slice(hashAt);
  const rest = hashAt === -1 ? href : href.slice(0, hashAt);
  const queryAt = rest.indexOf("?");
  const pathname = queryAt === -1 ? rest : rest.slice(0, queryAt);
  const search = queryAt === -1 ? "" : rest.slice(queryAt);
  return withLens({ pathname, params: new URLSearchParams(search).entries(), hash }, key);
}

/** The address a `Location` describes, in the shape `withLens` takes. */
export function hereOf(loc: { pathname: string; search: string; hash: string }) {
  return { pathname: loc.pathname, params: Array.from(new URLSearchParams(loc.search).entries()), hash: loc.hash };
}

/**
 * THE REQUEST HEADERS A PAGE IS RENDERED WITH: the ones the browser sent, with
 * `LENS_HEADER` set to this URL's `?as=` — or removed, when the URL names no
 * key-shaped lens — and `URL_HEADER` set to the path and query. Always
 * overwritten, so a header a browser forged never reaches a page the proxy saw
 * (proxy.ts).
 *
 * The proxy does NOT write the cookie, though it was once meant to. It cannot
 * tell whether the session holds a lens (the token carries no role, and
 * the proxy deliberately reads no user cache), nor whether the key names a lens
 * the roster offers, and it also sees prefetches — so a cookie written here
 * could be a key that names nothing, or the lens of a page nobody opened. The
 * browser writes it instead, from the lens it has resolved and is showing
 * (components/lens-url-sync.tsx).
 */
export function lensRequestHeaders(sent: Headers, url: { pathname: string; search: string }): Headers {
  const headers = new Headers(sent);
  const as = new URLSearchParams(url.search).get(LENS_PARAM);
  if (isLensKeyShaped(as)) headers.set(LENS_HEADER, as);
  else headers.delete(LENS_HEADER);
  headers.set(URL_HEADER, `${url.pathname}${url.search}`);
  return headers;
}

/**
 * The request header the proxy copies the page's path and query onto, for the
 * root layout — which, like every layout, is not handed `searchParams`, and has
 * to know the address to send a document load on to (`lensDocumentRedirect`).
 * Overwritten on every request the proxy sees, like LENS_HEADER.
 */
export const URL_HEADER = "x-ironside-url";

/**
 * WHERE A LENS HOLDER'S DOCUMENT LOAD THAT NAMES NO LENS IS SENT: the same address
 * with the key of the lens it is about to be drawn through — or null, to render
 * it as it is. The root layout asks, and redirects (app/layout.tsx).
 *
 * ⚠️ WHY THE SERVER, WHEN THE BROWSER STAMPS EVERYTHING ELSE
 * (components/lens-url-sync.tsx). A stamp is a navigation, and on a page just
 * loaded it is a navigation IN FLIGHT: when the next thing happens — another
 * page load, a scroll, a click — it races it. On the built image the browser
 * aborted the stamp's fetch as the next document load began, and Next answers a
 * failed fetch by loading the stamped address as a whole document, cancelling
 * the load that was asked for (CI, e2e admin 1/3: "Navigation to
 * /sourcing-export is interrupted by another navigation to
 * /onboarding-export?as=admin"); and a stamp landing mid-test reset a scrolled
 * page to the top (rubric-comments.spec). A redirect before the first byte has
 * nothing to race. The browser still stamps what the server cannot see: a
 * client navigation to a link with no `?as=`.
 *
 * Only a DOCUMENT load (a navigation's RSC fetch re-renders no root layout, a
 * router.refresh() keeps the address it already has, and a server action must
 * not be redirected), never "/" (it redirects on its own and carries the key —
 * app/page.tsx), and only with the address the proxy handed over. The
 * `sec-fetch-dest` line is the one that decides: Next 16 hides `rsc` and
 * `next-router-prefetch` from headers(), so those two reads are always false
 * here (lib/server/store-seed.ts).
 */
export function lensDocumentRedirect(headers: Headers, key: string): string | null {
  if (headers.get("sec-fetch-dest") !== "document") return null;
  if (headers.has("rsc") || headers.has("next-router-prefetch") || headers.has("next-action")) return null;
  const at = headers.get(URL_HEADER);
  if (!at) return null;
  const url = new URL(at, "http://x");
  if (url.pathname === "/") return null;
  return withLens({ pathname: url.pathname, params: url.searchParams.entries(), hash: "" }, key);
}
